import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { withRouter, Link } from 'react-router-dom';
import qs from 'query-string';
import { IconButton, Grid } from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBack';
import Box from '@material-ui/core/Box';
import * as actions from 'actions';
import * as bookingsSelectors from 'selectors/bookings';
import ActionFooter from 'components/BookingInfo/ActionFooter/index';
import BookingInfo from 'components/BookingInfo/index';
import CircularProgress from 'components/shared/CircularProgress';
import Errors from 'components/shared/Errors';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import TransactionStatus from 'components/shared/TransactionStatus';
import MangoImage from 'assets/img/powered-by-mangopay.png';
import PaymethodsImage from 'assets/img/paymethods.png';
import { isPayinLocked } from 'modules/bookings/helpers/payinLock';

// A booking's cottage snapshot can carry a null title_sv, and the header used to
// interpolate it straight into a template literal -- rendering the word "null"
// beside the booking number. Drop the title instead, keeping the number.
export const headerTitle = (cottage, bookingId) =>
  [cottage.title_sv, bookingId && `#${bookingId}`].filter(Boolean).join(' ');

export class BookingInfoContainer extends React.Component {
  componentDidMount() {
    const { bookingId } = this.props.match.params;
    this.props.fetchBookingRequest({ bookingId });
  }

  onAccept = (payment_method, card, klarna_addressline1, klarna_city, klarna_postalcode) => {
    this.props.bookingOfferStaleClear();
    const { bookingId } = this.props.match.params;
    this.props.bookingActionRequest({
      bookingId,
      version: this.props.booking.version,
      action: 'payment',
      payment_method,
      card,
      klarna_addressline1,
      klarna_city,
      klarna_postalcode,
    });
  };

  onCancel = (values) => {
    this.props.bookingOfferStaleClear();
    const { bookingId } = this.props.match.params;
    const { cancellation_reason } = values;
    this.props.bookingActionRequest({
      bookingId,
      version: this.props.booking.version,
      action: 'cancel',
      cancellation_reason,
    });
  };

  render() {
    const {
      booking,
      cancellationPolicy,
      errors,
      isHost,
      isPosting,
      location,
      success,
      isBookingStrict,
      offerStale,
    } = this.props;

    const { transaction, errorCode, errorMessage } = qs.parse(location.search);

    const { conversationId } = this.props.match.params;
    const { url } = this.props.match;
    const cottage = pick(booking.cottage, ['title_sv', 'id']);

    const images = booking.cottage?.images || [];
    if (booking.id && images.length > 0) {
      cottage.cottagePic = `/img/${images[0].filename}?w=600&h=600`;
    }

    return (
      <Main
        title={
          <div>
            <IconButton color="inherit" component={Link} to={`/conversations/${conversationId}`}>
              <ArrowBack />
            </IconButton>
            {headerTitle(cottage, booking.id)}
          </div>
        }
      >
        {!!transaction && !!booking && (
          <Box m={1}>
            <TransactionStatus
              booking={booking}
              status={transaction}
              errorCode={errorCode}
              errorMessage={errorMessage}
            />
          </Box>
        )}
        {!booking.id && <CircularProgress />}
        {!!booking.id && (
          <>
            <BookingInfo
              booking={booking}
              isHost={isHost}
              cancellationPolicy={cancellationPolicy}
              cottage={cottage}
            />
            <ActionFooter
              booking={booking}
              isHost={isHost}
              url={url}
              isBookingStrict={isBookingStrict}
              disableButtons={isPosting}
              offerChanged={offerStale}
              payinLocked={isPayinLocked(booking)}
              onClearOfferChanged={this.props.bookingOfferStaleClear}
              onCancel={this.onCancel}
              onAccept={this.onAccept}
            />
            <Grid container justifyContent="center">
              <Grid item xs={8} sm={4} md={2} lg={2}>
                <img src={PaymethodsImage} alt="Klarna" />
              </Grid>
            </Grid>
            <Grid container justifyContent="center">
              <Grid item xs={8} sm={6} md={4} lg={4}>
                <img src={MangoImage} alt="Mangopay" />
              </Grid>
            </Grid>
          </>
        )}
        <Success success={success} />
        <Errors errors={errors} />
      </Main>
    );
  }
}

BookingInfoContainer.propTypes = {
  booking: PropTypes.shape({
    id: PropTypes.number,
    version: PropTypes.number,
    cottage: PropTypes.shape({
      images: PropTypes.arrayOf(
        PropTypes.shape({
          filename: PropTypes.string,
        }),
      ),
    }),
    cancelled_at: PropTypes.string,
  }).isRequired,
  cancellationPolicy: PropTypes.shape({}),
  errors: PropTypes.shape({}),
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({
      bookingId: PropTypes.string.isRequired,
      conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  isHost: PropTypes.bool,
  isPosting: PropTypes.bool,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  success: PropTypes.shape({}),
  fetchBookingRequest: PropTypes.func.isRequired,
  isBookingStrict: PropTypes.bool,
  bookingActionRequest: PropTypes.func.isRequired,
  offerStale: PropTypes.bool,
  bookingOfferStaleClear: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const booking = bookingsSelectors.singleBooking(state);
  let cancellationPolicy = null;
  if (!isEmpty(booking) && !isEmpty(state.bookings.cancellationPolicy)) {
    cancellationPolicy = state.bookings.cancellationPolicy.find(
      (item) => item.id === booking.cancellation_policy_id,
    );
  }

  return {
    booking,
    cancellationPolicy,
    errors: bookingsSelectors.error(state),
    isHost: bookingsSelectors.isHost(state),
    isPosting: bookingsSelectors.isPosting(state),
    success: bookingsSelectors.success(state),
    isBookingStrict: bookingsSelectors.isBookingStrict(state),
    offerStale: bookingsSelectors.offerStale(state),
  };
};

const mapDispatchToProps = {
  fetchBookingRequest: actions.fetchBookingRequest,
  bookingActionRequest: actions.bookingActionRequest,
  bookingOfferStaleClear: actions.bookingOfferStaleClear,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BookingInfoContainer));
